import bridge from '@vkontakte/vk-bridge'

import type { IPlatformAdapter } from '../ports/IPlatformAdapter'
import type {
  BackButtonOptions,
  HapticStyle,
  MainButtonOptions,
  PlatformLaunchPayload,
  PlatformTheme,
} from '../ports/types'
import { openExternalUrl } from '../openExternalUrl'

type VkHapticSend =
  | { method: 'VKWebAppTapticNotificationOccurred', params: { type: 'error' | 'success' | 'warning' } }
  | { method: 'VKWebAppTapticSelectionChanged', params?: undefined }
  | { method: 'VKWebAppTapticImpactOccurred', params: { style: 'light' | 'medium' | 'heavy' } }

const VK_HAPTIC_BY_STYLE: Record<HapticStyle, VkHapticSend> = {
  error: {
    method: 'VKWebAppTapticNotificationOccurred',
    params: { type: 'error' },
  },
  success: {
    method: 'VKWebAppTapticNotificationOccurred',
    params: { type: 'success' },
  },
  warning: {
    method: 'VKWebAppTapticNotificationOccurred',
    params: { type: 'warning' },
  },
  selection: { method: 'VKWebAppTapticSelectionChanged' },
  light: {
    method: 'VKWebAppTapticImpactOccurred',
    params: { style: 'light' },
  },
  medium: {
    method: 'VKWebAppTapticImpactOccurred',
    params: { style: 'medium' },
  },
  heavy: {
    method: 'VKWebAppTapticImpactOccurred',
    params: { style: 'heavy' },
  },
  rigid: {
    method: 'VKWebAppTapticImpactOccurred',
    params: { style: 'medium' },
  },
  soft: {
    method: 'VKWebAppTapticImpactOccurred',
    params: { style: 'medium' },
  },
}

/**
 * VK Mini App adapter over `@vkontakte/vk-bridge`.
 * Missing chrome APIs (Main/Back button) are no-ops — UI must not branch.
 */
export class VkPlatformAdapter implements IPlatformAdapter {
  readonly kind = 'vk' as const

  private appearance: 'light' | 'dark' = 'light'
  private startParam: string | null = null
  private rawLaunch: string | null = null

  async init(): Promise<PlatformLaunchPayload> {
    try {
      await bridge.send('VKWebAppInit')
    }
    catch {
      // Outside embedded VK, init may fail — still try launch params.
    }

    this.rawLaunch = window.location.search?.replace(/^\?/, '') || null
    this.startParam = readVkStartParam()

    try {
      const config = await bridge.send('VKWebAppGetConfig') as {
        appearance?: 'light' | 'dark'
        scheme?: string
      }
      if (config.appearance === 'dark' || config.appearance === 'light') {
        this.appearance = config.appearance
      }
      else if (typeof config.scheme === 'string' && config.scheme.includes('dark')) {
        this.appearance = 'dark'
      }
    }
    catch {
      // theme stays default
    }

    return {
      kind: this.kind,
      raw: this.rawLaunch,
      startParam: this.startParam,
    }
  }

  getTheme(): PlatformTheme {
    return {
      colorScheme: this.appearance,
    }
  }

  setMainButton(_options: MainButtonOptions | null): void {
    // VK has no Telegram-style MainButton; chrome will use in-app primary (task 04).
  }

  setBackButton(_options: BackButtonOptions | null): void {
    // No-op — history back stays in Vue Router.
  }

  haptic(style: HapticStyle = 'light'): void {
    const send = VK_HAPTIC_BY_STYLE[style]
    void bridge
      .send(send.method, send.params)
      .catch(() => {
        // unsupported on this client
      })
  }

  close(): void {
    void bridge.send('VKWebAppClose', { status: 'success' }).catch(() => {})
  }

  openLink(url: string): void {
    if (!url) {
      return
    }
    // vk-bridge@3 has no typed VKWebAppOpenURL — open externally for payment links.
    openExternalUrl(url)
  }

  async requestContact(): Promise<string | null> {
    try {
      const result = await bridge.send('VKWebAppGetPhoneNumber') as {
        phone_number?: string
      }
      return result.phone_number ?? null
    }
    catch {
      return null
    }
  }

  getStartParam(): string | null {
    return this.startParam ?? readVkStartParam()
  }
}

function readVkStartParam(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  const hash = window.location.hash.replace(/^#/, '')
  if (hash && !hash.includes('=')) {
    return hash
  }
  const hashParams = new URLSearchParams(hash)
  const fromHash
    = hashParams.get('startapp')
      || hashParams.get('startParam')
      || hashParams.get('order')
  if (fromHash) {
    return fromHash
  }
  const search = new URLSearchParams(window.location.search)
  return search.get('startapp') || search.get('startParam') || null
}
